import { triadManager } from "@/services/triad/triad-manager";
import { skillManager } from "@/services/skill/skill-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { defineStore } from "pinia";
import { useMainStore } from "@/store/main/main";
import { useQuestionnaireStore } from "@/store/questionnaire/questionnaire";
import { useTrainingCourseStore } from "@/store/training-course/training-course";
import { mainStore } from "@/main";
import * as Sentry from "@sentry/vue";
import router from "@/router";
import { activityWorkflowStatusConstante } from "@/constantes/contractActivity";

export const useTriadStore = defineStore({
  id: "Triad",
  state: () => {
    return {
      triad: {},
      triads: {},
      contract: {},
      skills: {},
      subjects: {},
      webLinks: {},
      timelines: {},
      pedagogicDocuments: {},
      generalDocuments: {},
      documentCategories: {},
      documents: {},
      notepads: {},
      triadLoaded: false,
      sessions: [],
      currentSession: {},
      currentSessionSkillReply: {},
      currentReferential: {},
      loadingTriad: false,
      contractMissions: [],
      contractActivities: [],
      rncpSkillStatus: {},
      rncpSkillStatusVersion: 0,
    };
  },

  actions: {
    changeTriad(payload, design) {
      if (!payload.disableLoading) {
        useMainStore().setAppLoading(true);
      }
      if (payload.trainingCourseId) {
        useTrainingCourseStore().toggleTrainingCourse(payload.trainingCourseId, design);
      }
      window.localStorage.setItem("studea-oldTriad", payload.triadId);
      if (mainStore.isInternet) {
        return triadManager
          .loadOne(payload.triadId)
          .then((triad) => {
            if (triad && typeof triad === "object") {
              this.initTriadDatas(triad);
            }
          })
          .catch((error) => {
            if (!payload.disableLoading) {
              useMainStore().setAppLoading(false);
            }
            throw error;
          });
      } else {
        return Promise.resolve();
      }
    },

    goDashboard(triadId, trainingCourseId, currentRouter = router) {
      if (trainingCourseId) {
        return new Promise((resolve) => {
          const oldTriad = window.localStorage.getItem("studea-oldTriad")
            ? parseInt(window.localStorage.getItem("studea-oldTriad"))
            : null;
          if (!this.triad.id || triadId !== oldTriad) {
            this.loadingTriad = true;
            this.changeTriad({
              triadId: triadId,
              trainingCourseId: trainingCourseId,
            })
              .then(() => {
                resolve(true);
                currentRouter.push({ name: "Dashboard" });
                setTimeout(() => {
                  this.loadingTriad = false;
                }, 1000);
              })
              .catch(() => {
                this.loadingTriad = false;
                resolve(false);
              });
          } else {
            resolve(true);
            currentRouter.push({
              name: "Dashboard",
            });
          }
        });
      }
    },

    setNotepads(list) {
      this.notepads = list;
    },

    initTriadDatas(triad) {
      Sentry.setTag("triad", triad.id);
      // Un refresh silencieux du même triad (ex. retour sur le dashboard via la nav du bas, copie/suppression
      // de questionnaire) ne doit pas écraser les missions/activités déjà chargées par DashboardView : ce
      // rechargement est concurrent à celui de DashboardView et écrasait parfois des données fraîches par un
      // tableau vide, faisant "disparaître" les missions/activités jusqu'au F5 (cf. TA-4666).
      const isSameTriad = !!this.triad?.id && this.triad.id === triad.id;
      this.triad = triad;
      this.skills =
        triad.trainingCourse && triad.trainingCourse.skillCategories ? triad.trainingCourse.skillCategories : {};
      this.subjects = triad.trainingCourse && triad.trainingCourse.subjects ? triad.trainingCourse.subjects : {};
      this.webLinks = triad.trainingCourse && triad.trainingCourse.webLinks ? triad.trainingCourse.webLinks : {};
      if (triad.contracts && Array.isArray(Object.values(triad.contracts)) && Object.keys(triad.contracts).length) {
        this.contract =
          triad?.contracts && Object.values(triad.contracts).length ? Object.values(triad.contracts).at(-1) : {};
      } else {
        this.contract = {};
      }
      this.timelines = triad.timelines;
      this.notepads = triad.notepads;
      this.sessions = triad.sessions;
      this.setDocuments(triad);

      if (!isSameTriad) {
        this.contractMissions = [];
        this.contractActivities = [];
        this.rncpSkillStatus = {};
      }

      useQuestionnaireStore().setQuestionnaireToDoAndProgress(triad);
      useTrainingCourseStore().setTrainingCourseInfos(triad);

      setTimeout(function () {
        useMainStore().setAppLoading(false);
      }, 500);
    },

    sendSkills(skills, currentSessionId) {
      useMainStore().savingRequest();
      return skillManager.postSkills(skills, currentSessionId).then((success) => {
        notificationManager.showNotification("success", i18n.global.t("Skills saved successfully"));
        useMainStore().savingSuccess();
        return success;
      });
    },

    fetchSkills() {
      return skillManager.getTriadSkills().then((skills) => {
        this.skills = skills.skillCategories;
        this.triad.lastSkillReply = skills.lastSkillReply;
        if (this.currentSession && this.currentSession.id) {
          const newCurrentSession = skills.skillSessions.find((session) => session.id === this.currentSession.id);
          this.setCurrentSession(newCurrentSession);
        }
        return skills;
      });
    },

    setDocuments(triad) {
      this.documents = triad.documents;
      const pedagogicDocuments = triad.documents.find((item) => item.code === "pedagogic");
      if (pedagogicDocuments) {
        this.pedagogicDocuments = pedagogicDocuments.documents;
      } else {
        this.pedagogicDocuments = {};
      }

      const generalDocuments = triad.documents.find((item) => item.code === "general");
      if (generalDocuments) {
        this.generalDocuments = generalDocuments.documents;
      } else {
        this.generalDocuments = {};
      }

      const documentCategories = triad.documents.filter((item) => !["pedagogic", "general"].includes(item.code));
      if (documentCategories) {
        this.documentCategories = documentCategories;
      } else {
        this.documentCategories = {};
      }
    },

    setTriad(triad) {
      this.triad = triad;
      this.triadLoaded = true;
    },

    setCurrentSession(currentSession) {
      this.currentSession = currentSession;
    },

    setCurrentSessionSkillReply(currentSessionSkillReply) {
      this.currentSessionSkillReply = currentSessionSkillReply;
    },

    setCurrentReferential(currentReferential) {
      this.currentReferential = currentReferential;
    },

    findContractAndUpdateValidationStatus(contractId, status) {
      if (
        this.triad &&
        this.triad.contracts &&
        Array.isArray(this.triad.contracts) &&
        this.triad.contracts.find((contract) => contract.id === contractId)
      ) {
        this.triad.contracts.find((contract) => contract.id === contractId).ruptureJustificationValidated = status;
      }
    },

    deleteDocument(id, code) {
      const currentTabIndex = this.documents.findIndex((tab) => tab.code === code);
      if (currentTabIndex === -1) {
        return;
      }
      this.documents[currentTabIndex].documents = this.documents[currentTabIndex].documents.filter(
        (document) => document.id !== id,
      );
    },

    removeOneTimeline(id) {
      this.timelines = this.timelines.filter((timeline) => {
        return timeline.id !== id;
      });
    },

    setContractMissions(contractMissions) {
      this.contractMissions = contractMissions;
    },

    setRncpSkillStatus(missions) {
      this.rncpSkillStatus = Object.fromEntries(
        (missions ?? []).map((mission) => [mission.id, mission.validatedSkillCategories ?? []]),
      );
    },

    invalidateRncpSkillStatus() {
      this.rncpSkillStatusVersion++;
    },

    addContractMission(contractMission) {
      this.contractMissions.push(contractMission);
    },

    getContractMission(id) {
      return this.contractMissions.find((item) => parseInt(item.id) === parseInt(id));
    },

    updateContractMission(contractMission) {
      let itemToUpdate = this.getContractMission(contractMission.id);
      if (!itemToUpdate) {
        return;
      }
      Object.keys(itemToUpdate).forEach((k) => (itemToUpdate[k] = contractMission[k] ?? itemToUpdate[k]));
    },

    removeContractMission(id) {
      this.contractMissions = this.contractMissions.filter((item) => id !== item.id);
    },

    setContractMissionRemoved(id, removed) {
      const contractMission = this.getContractMission(id);
      if (!contractMission) {
        return;
      }
      contractMission.removed = removed;
    },

    setContractActivities(contractActivities) {
      this.contractActivities = contractActivities;
    },

    addContractActivity(contractActivity) {
      this.contractActivities.push(contractActivity);
    },

    getContractActivity(id) {
      return this.contractActivities.find((item) => parseInt(item.id) === parseInt(id));
    },

    updateContractActivity(contractActivity) {
      let itemToUpdate = this.getContractActivity(contractActivity.id);
      if (!itemToUpdate) {
        return;
      }
      Object.keys(itemToUpdate).forEach((k) => (itemToUpdate[k] = contractActivity[k] ?? itemToUpdate[k]));
    },

    removeContractActivity(id) {
      this.contractActivities = this.contractActivities.filter((item) => id !== item.id);
    },

    // ST143-TI-18448 : l'archivage (soft delete) ne recharge plus /contract-activity/list après
    // coup - contrairement à removeContractActivity(), l'activité reste dans le store (forcée
    // CANCELLED, `removed` remis à false pour ne pas rester masquée par la directive v-hide) afin
    // d'apparaître sous le filtre "Archivées" sans attendre un rechargement. Déplacée en fin de
    // liste pour rester cohérente avec l'ordre posé par les deux appels séquentiels de
    // getContractActivities() (actives d'abord, archivées ensuite), dont dépend l'index de tri.
    archiveContractActivity(id) {
      const index = this.contractActivities.findIndex((item) => item.id === id);
      if (index === -1) {
        return;
      }
      const [activity] = this.contractActivities.splice(index, 1);
      activity.workflowStatus = activityWorkflowStatusConstante.CANCELLED;
      activity.removed = false;
      this.contractActivities.push(activity);
    },

    setContractActivityRemoved(id, removed) {
      const contractActivity = this.getContractActivity(id);
      if (!contractActivity) {
        return;
      }
      contractActivity.removed = removed;
    },

    setContractActivityLoading(id, loading) {
      const contractActivity = this.getContractActivity(id);
      if (!contractActivity) {
        return;
      }
      contractActivity.loading = loading;
    },

    init() {
      this.triad = {};
      this.triads = {};
      this.contract = {};
      this.skills = {};
      this.subjects = {};
      this.webLinks = {};
      this.timelines = {};
      this.pedagogicDocuments = {};
      this.generalDocuments = {};
      this.documentCategories = {};
      this.documents = {};
      this.notepads = {};
      this.triadLoaded = false;
      this.sessions = {};
      this.currentSession = {};
      this.currentSessionSkillReply = {};
      this.currentReferential = {};
      this.loadingTriad = false;
      this.contractMissions = [];
      this.contractActivities = [];
      this.rncpSkillStatus = {};
    },
  },
});
